import { EventHandler, SimpleEventHandler, isEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";
import { QueueResolver, QueueResolverMixin, isQueueResolver } from "@ciaopeople/cp-shatsu/Queues";
import { AuthProviderIdentifier } from "./AuthProviderIdentifier.js";
import { SessionObject } from "../session/SessionObject.js";
import { isStorableSession, StorableSession } from "../session/StorableSession.js";
import { isFunction, isObject } from "@ciaopeople/cp-shatsu/TypeChecking";
import { DataDeletionInfo } from "@api-commons/api/types/AuthDataTypes.js";
import { LoginProvider, PrompLoginCustomParameters } from "./direct_providers/LoginProvider.js";
import { NativeAppSetSessionCause } from "@api-commons/nativeapps/CpNativeAppIntegration.js";
import { ThirdPartyCientAuthData } from "./data/LoginData.js";


export const LoginManagerEvents = {
    LOGIN_STATUS_CHANGED: "loginStatusChanged",
    LOGIN_USER_ACTION_REQUIRED: "loginUserActionRequired"
};


export interface LoginManagerPublic {
    preloadProviders(ids: AuthProviderIdentifier[], async: boolean): Promise<void>;
    loginOrAutologin(providerId: AuthProviderIdentifier, customParams: PrompLoginCustomParameters): Promise<SessionObject>,
    loginWithTPUD(tpud: ThirdPartyCientAuthData, extra_params: any): Promise<SessionObject>,
    login(providerId: AuthProviderIdentifier, extra_params: any, customParams: PrompLoginCustomParameters):  Promise<SessionObject>,
    doOneTapLogin (providerId: AuthProviderIdentifier, extra_params: any):  Promise<SessionObject>,
    checkIfSessionChanged(): Promise<SessionObject>,
    thereWasASession(): Promise<boolean>,
    isAutologinLocked(): Promise<boolean>,
    logout(): Promise<void>
    dPD(checkField: string): Promise<DataDeletionInfo>;
    getLastLoginProvider(): Promise<string>;
    getProviderList(): Promise<AuthProviderIdentifier[]>;
    
}

export function isLoginManagerPublic(obj: any): obj is LoginManagerPublic {
    return isObject(obj) && 
            isFunction(obj['loginOrAutologin']) &&
            isFunction(obj['isAutologinLocked']) &&
            isFunction(obj['login']) &&
            isFunction(obj['loginWithTPUD']) &&
            isFunction(obj['doOneTapLogin']) &&
            isFunction(obj['checkIfSessionChanged']) &&
            isFunction(obj['thereWasASession']) &&
            isFunction(obj['logout']) &&
            isFunction(obj['dPD']);
}

export interface LoginManager extends EventHandler, LoginManagerPublic, QueueResolver<LoginManager> {
    start(): void,
    //?TODO: #13144 must be removed
    nativeAppSetSession(token: string): Promise<SessionObject>,
     //?TODO: #13144 must be removed
    nativeAppSetSessionWithProvider(provider: AuthProviderIdentifier, token: string, cause: NativeAppSetSessionCause, isNewUser: boolean): Promise<SessionObject>,
    onLoginStatusChange(fun: (session: StorableSession) => any): void
    onLoginUserActionRequired(fun: (a: any) => any): void
    getProviderListSync(): LoginProvider[];
    eventuallyRevoke(hint: string, providers: AuthProviderIdentifier[], isDeleteRequest: boolean): Promise<boolean>

    nativeAppSignIn(token: string, tpud: ThirdPartyCientAuthData);

    getNativeAppLoginPromise(): Promise<SessionObject>;
    
}


export function isLoginManager(obj: any): obj is LoginManager {
    return isObject(obj) && 
            isLoginManagerPublic(obj) &&
            isEventHandler(obj) &&
            isQueueResolver(obj) &&
            isFunction(obj['start']) &&
            isFunction(obj['nativeAppSetSession']) &&
            isFunction(obj['nativeAppSetSessionWithProvider']) &&
            isFunction(obj['onLoginStatusChange']) &&
            isFunction(obj['getNativeAppLoginPromise']) &&
            isFunction(obj['onLoginUserActionRequired']) &&
            isFunction(obj['getProviderListSync']);
}

export abstract class LoginManagerBaseImpl extends QueueResolverMixin<LoginManager, typeof SimpleEventHandler>(SimpleEventHandler) implements LoginManager {
    


    public abstract eventuallyRevoke(hint: string, providers: AuthProviderIdentifier[], isDeleteRequest: boolean): Promise<boolean>;
    public abstract start(): void;
    public abstract loginOrAutologin(providerId: AuthProviderIdentifier, customParams: PrompLoginCustomParameters): Promise<SessionObject>;
    public abstract login(providerId: AuthProviderIdentifier, extra_params: any, customParams: PrompLoginCustomParameters): Promise<SessionObject>;
    public abstract loginWithTPUD(tpud: ThirdPartyCientAuthData, extra_params: any): Promise<SessionObject>;
    public abstract doOneTapLogin(providerId: AuthProviderIdentifier, extra_params: any): Promise<SessionObject>;
    public abstract checkIfSessionChanged(): Promise<SessionObject>;
    public abstract isAutologinLocked(): Promise<boolean>;
    public abstract thereWasASession(): Promise<boolean>;
    public abstract logout(): Promise<void>;
    //?TODO: #13144 - must be removed
    public abstract nativeAppSetSession(token: string): Promise<SessionObject>;
    //?TODO: #13144 - must be removed
    public abstract nativeAppSetSessionWithProvider(provider: AuthProviderIdentifier, token: string, cause: NativeAppSetSessionCause, isNewUser: boolean): Promise<SessionObject>;
    public abstract dPD(checkField: string): Promise<DataDeletionInfo>;
    public abstract getProviderList(): Promise<AuthProviderIdentifier[]>;
    public abstract getLastLoginProvider(): Promise<string>;
    public abstract getProviderListSync(): LoginProvider[];
    public abstract preloadProviders(ids: AuthProviderIdentifier[], async: boolean): Promise<void>;
    public abstract nativeAppSignIn(token: string, tpud: ThirdPartyCientAuthData);
    public onLoginStatusChange(fun: (session: StorableSession) => any): void {       
        this.on(LoginManagerEvents.LOGIN_STATUS_CHANGED, (evt, data) => {
            if(isStorableSession(data)) fun(data);
            else fun(null);
        });
    }


    public abstract getNativeAppLoginPromise(): Promise<SessionObject>;
        
    

    protected triggerLoginStatusChange(session: StorableSession): void {
        this.trigger(LoginManagerEvents.LOGIN_STATUS_CHANGED, session);
    }

    public onLoginUserActionRequired(fun: (a: any) => any): void {
        this.on(LoginManagerEvents.LOGIN_USER_ACTION_REQUIRED, (evt, data) => fun(data));
    }
    protected triggerLoginUserActionRequired(actionData: any): void {
        this.trigger(LoginManagerEvents.LOGIN_USER_ACTION_REQUIRED, actionData);
    }
}